import { useCreatePartyMutation } from "Api/Mutations/Party/useCreatePartyMutation";
import { useCreatePartyRelationMutation } from "Api/Mutations/Party/useCreatePartyRelationMutation";
import { useReplacePartyRelationMutation } from "Api/Mutations/Party/useReplacePartyRelationMutation";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import {
  Gender,
  type CreatePartyDto,
  type RelationType,
} from "Infrastructure/Api/Api";
import { NO_VAT_NUMBER } from "Schemas/vatNumberSchema";
import { Resources, useResource } from "Translations/Resources";
import { EMPTY_PHONE } from "Utils/PhoneUtils";
import { toast } from "sonner";

type Props = {
  relationPublicID?: string | null;
  owningPartyPublicID?: string | null;
  relatedPartyPublicID?: string | null;
  onSuccessUpdate?: (relatedPartyPublicID: string) => void;
};

type UpdateRelatedPartyArgs = {
  relationType: RelationType;
  relatedPartyData?: CreatePartyDto;
};

type useAddOrChangeRelatedPartyReturn = {
  isPending: boolean;
  updateRelatedParty: (args: UpdateRelatedPartyArgs) => Promise<void>;
};

export const useAddOrChangeRelatedParty = ({
  relationPublicID,
  owningPartyPublicID,
  relatedPartyPublicID,
  onSuccessUpdate,
}: Props): useAddOrChangeRelatedPartyReturn => {
  const user = useLoggedInUser();
  const { t } = useResource();

  const { mutate: putPartyRelation, isPending: isPartyRelationUpdating } =
    useReplacePartyRelationMutation({
      onSuccess: () =>
        relatedPartyPublicID && onSuccessUpdate?.(relatedPartyPublicID),
      onError: () => toast.error(t(Resources.Common.SaveError)),
    });

  const { mutate: postPartyRelation, isPending: isPartyRelationCreating } =
    useCreatePartyRelationMutation({
      onSuccess: (res) => onSuccessUpdate?.(res.relatedPartyPublicID),
      onError: () => toast.error(t(Resources.Common.SaveError)),
    });

  const { mutateAsync: postParty, isPending: isPartyCreating } =
    useCreatePartyMutation();

  const updateRelatedParty = async ({
    relationType,
    relatedPartyData = {
      gender: Gender.NotSet,
      phone: EMPTY_PHONE,
      vatNumber: NO_VAT_NUMBER,
      otherNationalities: [],
    },
  }: UpdateRelatedPartyArgs) => {
    if (!user?.partyPublicID) {
      return;
    }

    if (relatedPartyPublicID && relationPublicID) {
      putPartyRelation({
        partyPublicID: relationPublicID,
        data: {
          publicID: relationPublicID,
          relatedPartyPublicID,
          relationType,
        },
      });
    } else {
      const createdParty = await postParty(relatedPartyData);

      postPartyRelation({
        partyPublicID: owningPartyPublicID ?? user.partyPublicID,
        data: {
          relatedPartyPublicID: createdParty.publicID,
          relationType,
        },
      });
    }
  };

  return {
    isPending:
      isPartyRelationUpdating || isPartyRelationCreating || isPartyCreating,
    updateRelatedParty,
  };
};
